// src/components/PlLedgerDrill.tsx — 経営PL「元帳で深掘り」 (2026-09-26)
//
// 「委託費が下がったのはなぜ？」「人件費が下がったのはなぜ？」 に、 経理の元帳 (仕訳) で答える。
//   ① PL と元帳の対応 … 選んだ科目の PL 値と元帳合計を並べ、 差 (本部配賦・月ズレ) を明示する
//   ② 増減の内訳     … 比較期 → 当期 の増減を 科目 / 元帳科目 / 取引先 / 取引先×項目 に分解
//                       給料は元帳の「N名」 から 人数の増減 と 1人あたりの増減 に分ける
//   ③ 項目の中身     … 選んだ行の月次推移と仕訳 (摘要の個人名はビルド時に伏せ済み)
//
// ★内訳は元帳で計算する。 PL の月次値は各月締め時点・元帳は最新の締めなので、 単月では
//   月ズレ (前後の月との ±) が出る。 ① で差を必ず見せ、 内訳の合計が元帳の増減と一致することを表示する。
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Plot from "@observablehq/plot";
import { PlotFigure } from "./Plot";
import { plLedgerApi, usePlLedgerTarget } from "../lib/plLedgerApi";
import { PL_LEDGER_GROUPS, metricsOfSelection, type PlLedgerGroup, type PlLedgerItemRow } from "@shared/plLedger";
import { useSort, SortTh } from "../lib/useSort";

type Compare = "mom" | "yoy" | "ytd";
type GroupBy = "metric" | "code" | "vendor" | "item";

const GOOD = "#16a34a";
const BAD = "#dc2626";
const NEUTRAL = "#94a3b8";

// ── 月の計算 (YYYY-MM-01) ─────────────────────────────────────────
const addMonths = (d: string, n: number) => {
  const [y, m] = d.split("-").map(Number);
  const t = y * 12 + (m - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, "0")}-01`;
};
const fyStart = (d: string) => {
  const [y, m] = d.split("-").map(Number);
  return `${m >= 4 ? y : y - 1}-04-01`;
};
const monthsBetween = (from: string, to: string) => {
  const out: string[] = [];
  for (let d = from; d <= to; d = addMonths(d, 1)) out.push(d);
  return out;
};
const ym = (d: string) => { const [y, m] = d.split("-").map(Number); return `${y}年${m}月`; };
const ymShort = (d: string) => { const [y, m] = d.split("-").map(Number); return `${String(y).slice(2)}/${m}`; };

function periods(month: string, cmp: Compare): { cur: string[]; base: string[]; curLabel: string; baseLabel: string } {
  if (cmp === "mom") return { cur: [month], base: [addMonths(month, -1)], curLabel: ym(month), baseLabel: ym(addMonths(month, -1)) };
  if (cmp === "yoy") return { cur: [month], base: [addMonths(month, -12)], curLabel: ym(month), baseLabel: ym(addMonths(month, -12)) };
  const cur = monthsBetween(fyStart(month), month);
  const base = cur.map((d) => addMonths(d, -12));
  return { cur, base, curLabel: `${ym(cur[0])}〜${ym(month)}`, baseLabel: `${ym(base[0])}〜${ym(base[base.length - 1])}` };
}

const fmtK = (v: number | null | undefined) =>
  v == null || !isFinite(v) ? "—" : Math.round(v).toLocaleString("ja-JP");
const fmtKs = (v: number | null | undefined) =>
  v == null || !isFinite(v) ? "—" : (v > 0 ? "+" : v < 0 ? "−" : "±") + Math.abs(Math.round(v)).toLocaleString("ja-JP");
const fmtYen = (v: number) => Math.round(v).toLocaleString("ja-JP");

// ── 分解の行 ─────────────────────────────────────────────────────
type Filter = { metric: string; code?: string; vendor?: string; item?: string };
type DRow = {
  key: string; label: string; sub: string;
  base: number; cur: number; diff: number;          // 千円 (符号は「費用なら増=悪」 の向きのまま)
  hBase: number | null; hCur: number | null;         // 給料の人数 (本給の行の「N名」)
  headEffect: number | null; unitEffect: number | null;
  status: "新規" | "終了" | "増" | "減" | "横ばい";
  filters: Filter[];
  hidden: boolean;
};

function keyOf(r: PlLedgerItemRow, by: GroupBy): { key: string; label: string; sub: string } {
  if (by === "metric") return { key: r.metric, label: r.metric, sub: "" };
  if (by === "code") return { key: `${r.metric}|${r.code}`, label: r.acct_name, sub: r.metric };
  if (by === "vendor") {
    const v = r.vendor || r.item;
    return { key: `v|${v}`, label: v, sub: r.vendor ? "" : "(取引先なし・摘要)" };
  }
  return { key: `${r.metric}|${r.vendor}|${r.item}`, label: r.vendor ? `${r.vendor} / ${r.item}` : r.item, sub: r.metric };
}
function filterOf(r: PlLedgerItemRow, by: GroupBy): Filter {
  if (by === "metric") return { metric: r.metric };
  if (by === "code") return { metric: r.metric, code: r.code };
  if (by === "vendor") return r.vendor ? { metric: r.metric, vendor: r.vendor } : { metric: r.metric, vendor: "", item: r.item };
  return { metric: r.metric, vendor: r.vendor, item: r.item };
}

function decompose(rows: PlLedgerItemRow[], by: GroupBy, cur: Set<string>, base: Set<string>,
                   sign: (m: string) => number): DRow[] {
  // hmc/hmb = 人数が取れた月 (期間の全月で取れたときだけ人数×1人あたりに分解する。 欠けた月があると平均が下振れする)
  const acc = new Map<string, DRow & { fk: Set<string>; hmc: Set<string>; hmb: Set<string> }>();
  for (const r of rows) {
    const inCur = cur.has(r.date), inBase = base.has(r.date);
    if (!inCur && !inBase) continue;
    const k = keyOf(r, by);
    let a = acc.get(k.key);
    if (!a) {
      a = { ...k, base: 0, cur: 0, diff: 0, hBase: null, hCur: null, headEffect: null, unitEffect: null,
            status: "横ばい", filters: [], hidden: r.hidden, fk: new Set(), hmc: new Set(), hmb: new Set() };
      acc.set(k.key, a);
    }
    const v = (sign(r.metric) * r.amount) / 1000;
    if (inCur) { a.cur += v; if (r.headcount != null) { a.hCur = (a.hCur ?? 0) + r.headcount; a.hmc.add(r.date); } }
    if (inBase) { a.base += v; if (r.headcount != null) { a.hBase = (a.hBase ?? 0) + r.headcount; a.hmb.add(r.date); } }
    const f = filterOf(r, by);
    const fk = JSON.stringify(f);
    if (!a.fk.has(fk)) { a.fk.add(fk); a.filters.push(f); }
    a.hidden = a.hidden && r.hidden;
  }
  const nCur = cur.size, nBase = base.size;
  return [...acc.values()].map(({ fk: _fk, hmc, hmb, ...a }) => {
    a.diff = a.cur - a.base;
    const eps = 0.5;
    a.status = Math.abs(a.base) < eps && Math.abs(a.cur) >= eps ? "新規"
      : Math.abs(a.cur) < eps && Math.abs(a.base) >= eps ? "終了"
      : a.diff >= eps ? "増" : a.diff <= -eps ? "減" : "横ばい";
    // 人数 × 1人あたり の分解 (期間が複数月なら人数は月平均)
    if (a.hBase && a.hCur && hmc.size === nCur && hmb.size === nBase) {
      const h0 = a.hBase / nBase, h1 = a.hCur / nCur;
      const u0 = a.base / a.hBase;                                 // 比較期の 1人月あたり (千円)
      a.headEffect = (h1 - h0) * u0 * nCur;
      a.unitEffect = a.diff - a.headEffect;
      a.hBase = h0; a.hCur = h1;
    } else {
      // 人数が欠けた月がある → 人数は出さない (「—」)。 分解もしない
      a.hBase = a.hBase && hmb.size === nBase ? a.hBase / nBase : null;
      a.hCur = a.hCur && hmc.size === nCur ? a.hCur / nCur : null;
    }
    return a;
  });
}

// ─────────────────────────────────────────────────────────────────
export function PlLedgerDrill({ asOfMonth }: { asOfMonth: string }) {
  const sectionRef = useRef<HTMLElement>(null);
  const [groupKey, setGroupKey] = useState<string>("委託費計");
  const [metric, setMetric] = useState<string | null>(null);
  const [cmp, setCmp] = useState<Compare>("yoy");
  const [by, setBy] = useState<GroupBy>("metric");
  const [selKey, setSelKey] = useState<string | null>(null);
  const target = usePlLedgerTarget();

  // 差異ランキングからの遷移
  useEffect(() => {
    if (!target.group || target.seq === 0) return;
    setGroupKey(target.group);
    setMetric(target.metric);
    setBy(target.metric && !target.metric.startsWith("@") ? "item" : "metric");
    setSelKey(null);
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [target.seq]); // eslint-disable-line react-hooks/exhaustive-deps

  const group = PL_LEDGER_GROUPS.find((g) => g.key === groupKey) as PlLedgerGroup;
  const sign = (m: string) => group.sign?.[m] ?? 1;
  const scopeMetrics = metricsOfSelection(group, metric);
  const scopeTitle = !metric ? group.label : (group.subsets?.find((x) => x.key === metric)?.label ?? metric);

  const summary = useQuery({ queryKey: ["pl-ledger-summary"], queryFn: plLedgerApi.summary, staleTime: 5 * 60_000 });
  const items = useQuery({
    queryKey: ["pl-ledger-items", group.key],
    queryFn: () => plLedgerApi.items(group.metrics),
    staleTime: 5 * 60_000,
    enabled: !!summary.data?.available,
  });

  const months = summary.data?.months ?? [];
  const lastLedger = months[months.length - 1];
  const month = months.includes(asOfMonth) ? asOfMonth : (lastLedger && asOfMonth > lastLedger ? lastLedger : asOfMonth);
  const per = periods(month, cmp);
  const missing = [...per.cur, ...per.base].filter((d) => !months.includes(d));

  const drows = useMemo(() => {
    if (!items.data) return [];
    const rows = items.data.rows.filter((r) => scopeMetrics.includes(r.metric));
    return decompose(rows, by, new Set(per.cur), new Set(per.base), sign)
      .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
  }, [items.data, metric, by, per.cur.join(), per.base.join(), group.key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (summary.isLoading) return <section className="text-xs text-slate-400 animate-pulse">元帳 読み込み中…</section>;
  if (summary.error) {
    const msg = (summary.error as Error).message;
    if (msg.startsWith("403")) {
      return (
        <section id="pl-ledger-drill" className="border-t pt-4">
          <h3 className="text-sm font-semibold">🔎 元帳で深掘り</h3>
          <p className="text-xs text-slate-500 mt-1">仕訳の閲覧は現在 開発版の利用者と管理者に限っています。</p>
        </section>
      );
    }
    return <section className="text-xs text-red-700">元帳の取得に失敗: {msg}</section>;
  }
  if (!summary.data?.available || (summary.data.months ?? []).length === 0) {
    return (
      <section id="pl-ledger-drill" className="border-t pt-4">
        <h3 className="text-sm font-semibold">🔎 元帳で深掘り (未生成)</h3>
        <p className="text-xs text-amber-700 mt-1">
          元帳 CSV を <code>pl/input/元帳/</code> に置き、 <code>Rscript pl/script/pl_ledger.R</code> →
          <code>build_dashboard_db.R</code> を実行してください。
        </p>
      </section>
    );
  }

  const up = group.revenue ? GOOD : BAD;     // 増えたときの色
  const down = group.revenue ? BAD : GOOD;

  return (
    <section id="pl-ledger-drill" ref={sectionRef} className="border-t pt-4 space-y-4">
      <div>
        <h3 className="text-sm font-semibold">🔎 元帳で深掘り — PL の増減を仕訳まで分解
          <span className="ml-2 text-[11px] font-normal text-slate-400">単位: 千円 (仕訳は円) / 元帳 {ym(months[0])}〜{ym(lastLedger)}</span>
        </h3>
        <p className="text-[11px] text-slate-500 mt-0.5">
          経理の元帳 (仕訳) を PL の科目に対応づけています。 元帳に無いのは 医業収益・支払利息・本部配賦 (本部人件費/本部経費) です。
          個人に結びつく仕訳 (給与の個人別振替・社宅・慶弔・研修参加・患者の精算 など) は、 摘要を載せずに区分ごとに合算しています。
        </p>
      </div>

      {/* 操作 (1 行) */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
        <div className="flex flex-wrap gap-1">
          {PL_LEDGER_GROUPS.map((g) => (
            <button key={g.key} type="button"
              onClick={() => { setGroupKey(g.key); setMetric(null); setBy("metric"); setSelKey(null); }}
              className={`px-2 py-1 rounded border ${g.key === groupKey ? "bg-slate-800 text-white border-slate-800" : "bg-white hover:bg-slate-50"}`}>
              {g.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1">科目
          <select className="border rounded px-1 py-0.5" value={metric ?? ""}
            onChange={(e) => { const m = e.target.value || null; setMetric(m); setBy(m && !m.startsWith("@") ? "item" : "metric"); setSelKey(null); }}>
            <option value="">{group.label} 全体</option>
            {group.subsets?.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
            {group.metrics.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <div className="flex gap-1" role="group" aria-label="比較">
          {([["mom", "前月比"], ["yoy", "前年同月比"], ["ytd", "年度累計の前年比"]] as const).map(([k, l]) => (
            <button key={k} type="button" onClick={() => { setCmp(k); setSelKey(null); }}
              className={`px-2 py-1 rounded border ${cmp === k ? "bg-sky-700 text-white border-sky-700" : "bg-white hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <label className="flex items-center gap-1">束ね方
          <select className="border rounded px-1 py-0.5" value={by} onChange={(e) => { setBy(e.target.value as GroupBy); setSelKey(null); }}>
            {scopeMetrics.length > 1 && <option value="metric">PL 科目</option>}
            <option value="code">元帳科目 (給料は職種)</option>
            <option value="vendor">取引先</option>
            <option value="item">取引先 × 項目 (給料は部署・職種)</option>
          </select>
        </label>
        <span className="text-slate-500">対象月 <b>{ym(month)}</b>{month !== asOfMonth && <span className="text-amber-700"> (元帳の最新月)</span>}</span>
      </div>

      {missing.length > 0 ? (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          比較に必要な月の元帳がありません ({missing.map(ymShort).join(", ")})。 元帳は {ym(months[0])} からです。
          前年同月比・年度累計の前年比は {ym(addMonths(months[0], 12))} 以降で使えます。
        </p>
      ) : (
        <>
          <ReconBlock recon={summary.data.recon} group={group} metrics={scopeMetrics} title={scopeTitle}
                      whole={!metric} per={per} sign={sign} />
          {items.isLoading ? <div className="text-xs text-slate-400 animate-pulse">内訳を読み込み中…</div>
            : items.error ? <div className="text-xs text-red-700">内訳の取得に失敗: {(items.error as Error).message}</div>
            : <BreakdownBlock rows={drows} per={per} up={up} down={down} by={by} metric={metric}
            selKey={selKey} onSelect={(k) => setSelKey(k === selKey ? null : k)}
            onDrillMetric={(m) => { setMetric(m); setBy("item"); setSelKey(null); }} />}
          {selKey && (() => {
            const row = drows.find((r) => r.key === selKey);
            return row ? <ItemDetail row={row} items={items.data?.rows ?? []} by={by} months={months}
                                     per={per} sign={sign} up={up} down={down} /> : null;
          })()}
        </>
      )}
    </section>
  );
}

// ── ① PL と元帳の対応 ───────────────────────────────────────────────
function ReconBlock({ recon, group, metrics, title, whole, per, sign }: {
  recon: { date: string; metric: string; pl: number | null; ledger: number | null }[];
  group: PlLedgerGroup; metrics: string[]; title: string; whole: boolean;
  per: ReturnType<typeof periods>; sign: (m: string) => number;
}) {
  const plOnly = whole ? group.plOnly : [];
  const sum = (ms: string[], months: string[], f: "pl" | "ledger") =>
    recon.filter((r) => ms.includes(r.metric) && months.includes(r.date))
      .reduce((s, r) => s + sign(r.metric) * (r[f] ?? 0), 0);
  const rows = [
    { label: `当期 ${per.curLabel}`, pl: sum(metrics, per.cur, "pl"), led: sum(metrics, per.cur, "ledger"), only: sum(plOnly, per.cur, "pl") },
    { label: `比較 ${per.baseLabel}`, pl: sum(metrics, per.base, "pl"), led: sum(metrics, per.base, "ledger"), only: sum(plOnly, per.base, "pl") },
  ];
  const d = { pl: rows[0].pl - rows[1].pl, led: rows[0].led - rows[1].led, only: rows[0].only - rows[1].only };
  const gap = Math.max(Math.abs(rows[0].pl - rows[0].led), Math.abs(rows[1].pl - rows[1].led));
  // 差が大きい科目を示す (どの科目が PL と元帳でずれているか)
  const gapBy = metrics.map((m) => ({
    m, g: [...per.cur, ...per.base].reduce((s, dt) => {
      const r = recon.find((x) => x.metric === m && x.date === dt);
      return s + Math.abs((r?.pl ?? 0) - (r?.ledger ?? 0));
    }, 0),
  })).filter((x) => x.g >= 1).sort((a, b) => b.g - a.g).slice(0, 4);
  return (
    <div>
      <div className="text-xs font-semibold mb-1">① PL と元帳の対応 — {title}</div>
      <table className="text-xs border">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-2 py-1 border text-left">期間</th>
            <th className="px-2 py-1 border text-right">PL ({title}{plOnly.length ? " 元帳対応分" : ""})</th>
            <th className="px-2 py-1 border text-right">元帳合計</th>
            <th className="px-2 py-1 border text-right">差 (PL−元帳)</th>
            {plOnly.length > 0 && <th className="px-2 py-1 border text-right">{plOnly.join("・")} (元帳外)</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td className="px-2 py-1 border">{r.label}</td>
              <td className="px-2 py-1 border text-right">{fmtK(r.pl)}</td>
              <td className="px-2 py-1 border text-right">{fmtK(r.led)}</td>
              <td className={`px-2 py-1 border text-right ${Math.abs(r.pl - r.led) >= 1 ? "text-amber-700 font-semibold" : "text-slate-400"}`}>{fmtKs(r.pl - r.led)}</td>
              {plOnly.length > 0 && <td className="px-2 py-1 border text-right text-slate-500">{fmtK(r.only)}</td>}
            </tr>
          ))}
          <tr className="bg-slate-50 font-semibold">
            <td className="px-2 py-1 border">増減</td>
            <td className="px-2 py-1 border text-right">{fmtKs(d.pl)}</td>
            <td className="px-2 py-1 border text-right">{fmtKs(d.led)}</td>
            <td className="px-2 py-1 border text-right">{fmtKs(d.pl - d.led)}</td>
            {plOnly.length > 0 && <td className="px-2 py-1 border text-right">{fmtKs(d.only)}</td>}
          </tr>
        </tbody>
      </table>
      <p className="text-[11px] text-slate-500 mt-1">
        {gap < 1
          ? "✅ この期間は PL と元帳が千円単位で一致しています。 下の内訳の合計が PL の増減です。"
          : <>⚠ PL と元帳に差があります{gapBy.length > 0 && <> (主に {gapBy.map((x) => `${x.m} ${fmtK(x.g)}`).join("・")})</>}。
             PL の月次値は各月の締め時点、 元帳は最新の締め (後日の訂正・伝票日付の付け替えを反映) なので、
             単月の差の多くは前後の月との ± (月ズレ) です。 ただし
             {metrics.includes("賞与") ? " 賞与 (年度によって PL=支給基準・元帳=月額引当) や" : ""} 固定資産税等・受取利息・消耗品費 など、
             年度を通しても残る差もあります。 下の内訳は元帳の増減を分解したものです。</>}
      </p>
    </div>
  );
}

// ── ② 増減の内訳 ──────────────────────────────────────────────────
function BreakdownBlock({ rows, per, up, down, by, metric, selKey, onSelect, onDrillMetric }: {
  rows: DRow[]; per: ReturnType<typeof periods>; up: string; down: string; by: GroupBy; metric: string | null;
  selKey: string | null; onSelect: (k: string) => void; onDrillMetric: (m: string) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const total = rows.reduce((s, r) => s + r.diff, 0);
  const moved = rows.filter((r) => Math.abs(r.diff) >= 0.5);
  const top = moved.slice(0, 15);
  const restN = moved.length - top.length;
  const rest = moved.slice(15).reduce((s, r) => s + r.diff, 0);
  const hasHead = rows.some((r) => r.headEffect != null);
  const s = useSort(showAll ? rows : moved.slice(0, 40), { resetKey: `${by}|${metric}|${per.cur.join()}|${showAll}` });

  const chartData = [
    ...top.map((r) => ({ label: r.label, diff: r.diff, key: r.key })),
    ...(restN > 0 ? [{ label: `その他 ${restN} 件`, diff: rest, key: "" }] : []),
  ];
  const opts: Plot.PlotOptions = {
    height: Math.max(120, chartData.length * 22 + 40),
    width: 900,
    marginLeft: 260,
    marginRight: 60,
    x: { label: "増減 (千円)", grid: true, tickFormat: (v: number) => v.toLocaleString("ja-JP") },
    y: { label: null, domain: chartData.map((d) => d.label), tickFormat: (t: string) => (t.length > 24 ? t.slice(0, 23) + "…" : t) },
    marks: [
      Plot.ruleX([0], { stroke: "#64748b" }),
      Plot.barX(chartData, {
        y: "label", x: "diff", rx: 2,
        fill: (d: { diff: number; key: string }) => (d.key === "" ? NEUTRAL : d.diff >= 0 ? up : down),
        insetTop: 2, insetBottom: 2,
      }),
      Plot.text(chartData, {
        y: "label", x: "diff", text: (d: { diff: number }) => fmtKs(d.diff),
        textAnchor: "start", dx: 4, fontSize: 10, fill: "#334155",
        filter: (d: { diff: number }) => d.diff >= 0,
      }),
      // 減った行の数値は 0 線の右に置く (棒の左端に置くと科目名と重なる。 同じ行の右側は空いている)
      Plot.text(chartData, {
        y: "label", x: 0, text: (d: { diff: number }) => fmtKs(d.diff),
        textAnchor: "start", dx: 4, fontSize: 10, fill: "#334155",
        filter: (d: { diff: number }) => d.diff < 0,
      }),
      Plot.tip(chartData, Plot.pointerY({ y: "label", x: "diff", title: (d: { label: string; diff: number }) => `${d.label}\n増減 ${fmtKs(d.diff)} 千円` })),
    ],
  };

  const byLabel = { metric: "PL 科目", code: "元帳科目", vendor: "取引先", item: "取引先 × 項目" }[by];
  return (
    <div>
      <div className="text-xs font-semibold mb-1">
        ② 増減の内訳 ({byLabel}) — {per.baseLabel} → {per.curLabel}
        <span className="ml-2 font-normal text-slate-500">元帳の増減 {fmtKs(total)} 千円
          {showAll ? " = 下の全行の合計" : " (下の表は増減の大きい順の上位。 全行の合計がこの値)"}</span>
      </div>
      {top.length === 0
        ? <p className="text-xs text-slate-500">増減はありません。</p>
        : <PlotFigure options={opts} responsiveWidth />}
      <div className="overflow-x-auto mt-2">
        <table className="text-xs border w-full">
          <thead className="bg-slate-50">
            <tr>
              <SortTh s={s} sortKey="label" get={(r) => (r as DRow).label} className="px-2 py-1 border text-left">{byLabel}</SortTh>
              <SortTh s={s} sortKey="status" get={(r) => (r as DRow).status} className="px-2 py-1 border">区分</SortTh>
              <SortTh s={s} sortKey="base" get={(r) => (r as DRow).base} className="px-2 py-1 border text-right">比較</SortTh>
              <SortTh s={s} sortKey="cur" get={(r) => (r as DRow).cur} className="px-2 py-1 border text-right">当期</SortTh>
              <SortTh s={s} sortKey="diff" get={(r) => (r as DRow).diff} className="px-2 py-1 border text-right">増減</SortTh>
              {hasHead && <>
                <SortTh s={s} sortKey="h" get={(r) => (r as DRow).hCur} className="px-2 py-1 border text-right">人数 比較→当期</SortTh>
                <SortTh s={s} sortKey="he" get={(r) => (r as DRow).headEffect} className="px-2 py-1 border text-right">うち人数</SortTh>
                <SortTh s={s} sortKey="ue" get={(r) => (r as DRow).unitEffect} className="px-2 py-1 border text-right">うち1人あたり</SortTh>
              </>}
            </tr>
          </thead>
          <tbody>
            {s.rows.map((r) => {
              const color = Math.abs(r.diff) < 0.5 ? "#64748b" : r.diff > 0 ? up : down;
              return (
                <tr key={r.key} className={`cursor-pointer hover:bg-sky-50 ${selKey === r.key ? "bg-sky-100" : ""}`}
                    onClick={() => (by === "metric" ? onDrillMetric(r.label) : onSelect(r.key))}
                    title={by === "metric" ? "この科目の内訳へ" : "月次推移と仕訳を表示"}>
                  <td className="px-2 py-1 border">
                    {r.label}{r.sub && <span className="ml-1 text-[10px] text-slate-400">{r.sub}</span>}
                    {by === "metric" && <span className="ml-1 text-sky-700">▶</span>}
                  </td>
                  <td className="px-2 py-1 border text-center text-[10px]">
                    <span className={`px-1 rounded ${r.status === "新規" ? "bg-amber-100" : r.status === "終了" ? "bg-slate-200" : ""}`}>{r.status}</span>
                  </td>
                  <td className="px-2 py-1 border text-right">{fmtK(r.base)}</td>
                  <td className="px-2 py-1 border text-right">{fmtK(r.cur)}</td>
                  <td className="px-2 py-1 border text-right font-semibold" style={{ color }}>{fmtKs(r.diff)}</td>
                  {hasHead && <>
                    <td className="px-2 py-1 border text-right">{r.hBase != null || r.hCur != null ? `${r.hBase?.toFixed(r.hBase % 1 ? 1 : 0) ?? "—"} → ${r.hCur?.toFixed(r.hCur % 1 ? 1 : 0) ?? "—"}` : ""}</td>
                    <td className="px-2 py-1 border text-right">{r.headEffect != null ? fmtKs(r.headEffect) : ""}</td>
                    <td className="px-2 py-1 border text-right">{r.unitEffect != null ? fmtKs(r.unitEffect) : ""}</td>
                  </>}
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
          <span>{showAll ? `全 ${rows.length} 行` : `増減のある上位 ${Math.min(40, moved.length)} 行 / 全 ${rows.length} 行`}</span>
          <button type="button" className="underline" onClick={() => setShowAll(!showAll)}>{showAll ? "上位だけに戻す" : "全行を表示"}</button>
          {hasHead && <span>人数 = 元帳の給与仕訳にある「N名」 (本給の行)。 うち人数 = 人数の増減 × 比較期の1人あたり、 うち1人あたり = 残り。</span>}
        </div>
      </div>
    </div>
  );
}

// ── ③ 項目の中身 (月次推移 + 仕訳) ─────────────────────────────────────
function ItemDetail({ row, items, by, months, per, sign, up, down }: {
  row: DRow; items: PlLedgerItemRow[]; by: GroupBy; months: string[];
  per: ReturnType<typeof periods>; sign: (m: string) => number; up: string; down: string;
}) {
  const monthly = useMemo(() => {
    const keys = new Set(row.filters.map((f) => JSON.stringify(f)));
    const m = new Map<string, number>();
    for (const r of items) {
      if (!keys.has(JSON.stringify(filterOf(r, by)))) continue;
      m.set(r.date, (m.get(r.date) ?? 0) + (sign(r.metric) * r.amount) / 1000);
    }
    return months.map((d) => ({
      d, label: ymShort(d), v: m.get(d) ?? 0,
      role: per.cur.includes(d) ? "当期" : per.base.includes(d) ? "比較" : "",
    }));
  }, [row, items, by, months, per, sign]);

  const lineMonths = [...per.base, ...per.cur];
  const lines = useQuery({
    queryKey: ["pl-ledger-lines", row.key, lineMonths.join()],
    queryFn: async () => {
      const fs = row.filters.slice(0, 8);
      const rs = await Promise.all(fs.map((f) => plLedgerApi.lines({ ...f, months: lineMonths })));
      return { rows: rs.flatMap((r) => r.rows).sort((a, b) => (a.date ?? a.ym + "~").localeCompare(b.date ?? b.ym + "~")),
               truncated: rs.some((r) => r.truncated) || row.filters.length > fs.length };
    },
    staleTime: 5 * 60_000,
  });

  const opts: Plot.PlotOptions = {
    height: 170, width: 900, marginLeft: 60,
    x: { label: null, domain: monthly.map((m) => m.label) },
    y: { label: "千円", grid: true, tickFormat: (v: number) => v.toLocaleString("ja-JP") },
    marks: [
      Plot.ruleY([0], { stroke: "#64748b" }),
      Plot.barY(monthly, {
        x: "label", y: "v", rx: 2, insetLeft: 2, insetRight: 2,
        fill: (d: { role: string }) => (d.role === "当期" ? "#0369a1" : d.role === "比較" ? "#7dd3fc" : "#cbd5e1"),
      }),
      Plot.tip(monthly, Plot.pointerX({ x: "label", y: "v", title: (d: { d: string; v: number; role: string }) => `${ym(d.d)}${d.role ? ` (${d.role})` : ""}\n${fmtK(d.v)} 千円` })),
    ],
  };
  return (
    <div className="border rounded p-3 bg-slate-50/50">
      <div className="text-xs font-semibold mb-1">③ {row.label}
        <span className="ml-2 font-normal" style={{ color: row.diff > 0 ? up : down }}>{fmtKs(row.diff)} 千円</span>
        <span className="ml-2 font-normal text-slate-400">濃い青=当期 / 薄い青=比較</span>
      </div>
      <PlotFigure options={opts} responsiveWidth />
      <div className="text-xs font-semibold mt-2 mb-1">仕訳 ({per.baseLabel} と {per.curLabel})</div>
      {lines.isLoading ? <div className="text-xs text-slate-400">読み込み中…</div>
        : lines.error ? <div className="text-xs text-red-700">{(lines.error as Error).message}</div>
        : (
        <div className="max-h-80 overflow-y-auto">
          <table className="text-xs border w-full">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-2 py-1 border">伝票日付</th>
                <th className="px-2 py-1 border">伝票No</th>
                <th className="px-2 py-1 border text-left">相手科目</th>
                <th className="px-2 py-1 border text-left">摘要</th>
                <th className="px-2 py-1 border text-right">金額 (円)</th>
              </tr>
            </thead>
            <tbody>
              {lines.data!.rows.map((l, i) => (
                <tr key={i} className={per.cur.includes(l.ym) ? "" : "text-slate-500"}>
                  <td className="px-2 py-1 border whitespace-nowrap">{l.date ?? `${ymShort(l.ym)} 合算`}</td>
                  <td className="px-2 py-1 border text-right">{l.voucher_no ?? ""}</td>
                  <td className="px-2 py-1 border">{l.partner}</td>
                  <td className={`px-2 py-1 border ${l.hidden ? "italic text-slate-400" : ""}`}>{l.tekiyo}</td>
                  <td className="px-2 py-1 border text-right">{fmtYen(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {lines.data!.truncated && <p className="text-[11px] text-amber-700 mt-1">行数が多いため一部だけ表示しています。</p>}
        </div>
      )}
    </div>
  );
}
